import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const NewsSection = () => {
  const { t, language } = useLanguage();

  const newsImages = [
    "https://customer-assets.emergentagent.com/job_cyberyouth/artifacts/bza3no85_1-1.jpg",
    "https://customer-assets.emergentagent.com/job_cyberyouth/artifacts/v8ot0scj_2-1.webp",
    "https://customer-assets.emergentagent.com/job_cyberyouth/artifacts/n7nczbmc_3-1.webp",
    "https://customer-assets.emergentagent.com/job_cyberyouth/artifacts/5gto57j3_4-1.jpeg"
  ];

  const dates = language === 'tr' 
    ? ["20 Aralık 2024", "14 Ocak 2025", "2 Şubat 2025", "8 Şubat 2025"]
    : ["December 20, 2024", "January 14, 2025", "February 2, 2025", "February 8, 2025"];

  const newsKeys = ['news1', 'news2', 'news3', 'news4'];

  return (
    <section id="news" className="relative py-24 bg-gradient-to-b from-[#0a1628] to-[#060b1a]">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-[#4DD6FF]/10 rounded-full blur-[180px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full border border-[#4DFF88]/30 bg-[#4DFF88]/5 text-[#4DFF88] text-sm font-medium mb-4">
            {t('news.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
            {t('news.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('news.subtitle')}
          </p>
        </div>

        {/* News Cards - 4 items in 2x2 grid on larger screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {newsKeys.map((key, index) => (
            <Link 
              to={`/haber/${index + 1}`} 
              key={key}
              className="group relative overflow-hidden rounded-xl border border-white/10 bg-[#0a1628]/50 backdrop-blur-sm transition-all duration-500 hover:border-[#4DFF88]/30 hover:transform hover:-translate-y-2 block"
              data-testid={`news-card-${index + 1}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={newsImages[index]}
                  alt={t(`news.${key}.title`)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1628] via-transparent to-transparent" />
                
                {/* Date badge */}
                <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1 rounded-full bg-black/50 backdrop-blur-sm">
                  <Calendar className="w-4 h-4 text-[#4DFF88]" />
                  <span className="text-white text-xs font-medium">{dates[index]}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-base font-semibold text-white mb-3 group-hover:text-[#4DFF88] transition-colors duration-300 line-clamp-2 min-h-[48px]">
                  {t(`news.${key}.title`)}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                  {t(`news.${key}.excerpt`)}
                </p>
                <span className="p-0 h-auto text-[#4DFF88] font-medium flex items-center gap-2 text-sm">
                  {t('news.readMore')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>

              {/* Hover glow */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#4DFF88]/0 to-[#4DD6FF]/0 group-hover:from-[#4DFF88]/5 group-hover:to-[#4DD6FF]/5 pointer-events-none transition-all duration-500" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
